import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";

type Version = { version: number; at: string; user_name: string | null };

/**
 * base: /api/admin/directory (categories, departments, programs, state versions) or /api/admin/kb (problems, synonyms, intent rules).
 * deleted: the record is gone (a deleted synonym or intent rule), so its latest version, the copy kept at deletion, can be restored too.
 */
export function VersionsPanel({ type, id, base = "/api/admin/directory", deleted = false }: { type: string; id: string; base?: string; deleted?: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["versions", type, id], queryFn: () => api<{ versions: Version[] }>(`${base}/${type}/${id}/versions`) });
  const restore = useMutation({
    mutationFn: (v: number) => api(`${base}/${type}/${id}/versions/${v}/restore`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries(),
  });
  if (!q.data) return <p className="text-sm text-slate-500">Loading…</p>;
  const ask = (v: number) => (deleted ? `Restore version ${v}? The entry comes back and search uses it again.`
    : `Restore version ${v}? The record will need review again.`);
  return (
    <ul className="divide-y divide-slate-200 text-sm dark:divide-slate-800">
      {q.data.versions.map((v, i) => (
        <li key={v.version} className="flex items-center justify-between py-1.5">
          <span>v{v.version} · {new Date(v.at).toLocaleString()} · {v.user_name ?? "system"}
            {i === 0 && (deleted ? <span className="ml-2 text-xs text-red-700">deleted</span> : <span className="ml-2 text-xs text-emerald-700">current</span>)}</span>
          {(i > 0 || deleted) && <button className="btn-ghost text-xs" disabled={restore.isPending}
            onClick={() => { if (window.confirm(ask(v.version))) restore.mutate(v.version); }}>Restore</button>}
        </li>
      ))}
      {restore.error && <li role="alert" className="py-1.5 text-red-600">{(restore.error as Error).message}</li>}
    </ul>
  );
}
